import { getTablePermissions, hasPermission } from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";
import { router } from "expo-router";
import { MenuType } from "@/components/Table/types";

// Status options for incidents
const STATUS_OPTIONS = [
  { value: "PENDING REVIEW", color: "todo" },
  { value: "UNDER REVIEW", color: "pending" },
  { value: "ESCALATED", color: "attention" },
  { value: "RESOLVED", color: "complete" },
] as const;

const BILLING_STATUS_OPTIONS = [
  { value: "PENDING", color: "neutral" },
  { value: "READY FOR BILLING", color: "todo" },
  { value: "BILLING IN PROGRESS", color: "pending" },
  { value: "PNL PENDING", color: "complete" },
] as const;

const PROJECT_STATUS_OPTIONS = [
  { value: "POTENTIAL", color: "todo" },
  { value: "PENDING APPROVAL", color: "pending" },
  { value: "IN PROGRESS", color: "pending" },
  { value: "ON HOLD", color: "attention" },
  { value: "COMPLETED", color: "complete" },
  { value: "CONFLICTING", color: "attention" },
  { value: "LOST", color: "neutral" },
  { value: "CANCELED", color: "attention" },
] as const;

type FilterChoice = {
  name: string;
  kind: "text" | "number" | "boolean" | "date" | "decimal" | "enum" | "linked_record";
  menus?: MenuType[];
  displayName?: string;
  options?: readonly { readonly value: string; readonly color?: string }[];
  linkedRecord?: { labelKey?: string; valueKey?: string; truncate?: number };
};

export const getIncidentTableConfig = (userRoles: UserRoleType[] | null, displayNames) => {
  const permissions = getTablePermissions(userRoles, "incidents");

  // Build readonly array conditionally based on permissions
  const readonlyColumns: (string | [string, (key: string, value: any, row: any) => void])[] = [
    "id",
    "created_at",
    "occurred_date",
    "location",
    "project_status",
    "project_billing_status",
  ];

  // Only add clickable submitted_by_user column if user has users.read
  if (hasPermission(userRoles, "users", "read")) {
    readonlyColumns.push([
      "submitted_by_user",
      (_key: string, _value: any, row: any) =>
        row.submitted_by_user?.id && router.setParams({ panel: "user", id: row.submitted_by_user.id }),
    ]);
  }

  // Only add clickable project column if user has projects.read
  if (hasPermission(userRoles, "projects", "read")) {
    readonlyColumns.push([
      "project",
      (_key: string, _value: any, row: any) =>
        row.project?.id && router.setParams({ panel: "project", id: row.project.id }),
    ]);
  }

  return {
    entityName: "Incident",
    entityNamePlural: "Incidents",

    tableConfig: {
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return `${displayNames?.incidents?.name ?? "Incident"} Id`;
          case "submitted_by_user":
            return `Submitted By ${displayNames?.users?.name ?? "User"}`;
          case "project":
            return displayNames?.projects?.name ?? "Project";
          case "project_status":
            return `${displayNames?.projects?.name ?? "Project"} Status`;
          case "project_billing_status":
            return `${displayNames?.projects?.name ?? "Project"} Billing Status`;
          case "location":
            return displayNames?.locations?.name ?? "Location";
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      ignore: ["", "tenant_id", "meta_data", "comments", "history"],
      readonly: readonlyColumns,
      validators: {},
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      dataTypes: {
        created_at: "date" as const,
        occurred_date: "date" as const,
        submitted_by_user: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        project: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        location: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        status: {
          type: "enum" as const,
          options: [...STATUS_OPTIONS],
        },
        project_status: {
          type: "enum" as const,
          options: PROJECT_STATUS_OPTIONS,
        },
        project_billing_status: {
          type: "enum" as const,
          options: BILLING_STATUS_OPTIONS,
        },
      },
    },
    filterChoices: [
      {
        name: "id",
        kind: "text" as const,
        displayName: `${displayNames?.incidents?.name ?? "Incident"} Id`,
        menus: ["filter", "visible"] as const,
      },
      { name: "name", kind: "text", menus: ["filter", "visible", "orderby", "groupby"] },
      { name: "description", kind: "text", menus: ["filter", "visible"] as const },
      {
        name: "status",
        kind: "enum",
        options: [...STATUS_OPTIONS],
        menus: ["filter", "visible", "orderby", "groupby"],
      },
      { name: "incident_type", kind: "enum", displayName: "Type", menus: ["filter", "groupby", "visible"] },
      {
        name: "submitted_by_user",
        displayName: `Submitted By ${displayNames?.users?.name ?? "User"}`,
        kind: "linked_record",
        menus: ["filter", "groupby", "visible", "orderby"],
        linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
      },
      {
        name: "project",
        displayName: displayNames?.projects?.name ?? "Project",
        kind: "linked_record",
        menus: ["filter", "groupby", "visible", "orderby"],
        linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
      },
      {
        name: "project_status",
        displayName: `${displayNames?.projects?.name ?? "Project"} Status`,
        kind: "enum" as const,
        options: [...PROJECT_STATUS_OPTIONS],
        menus: ["filter", "visible", "groupby", "orderby"] as const,
      },
      {
        name: "project_billing_status",
        displayName: `${displayNames?.projects?.name ?? "Project"} Billing Status`,
        kind: "enum" as const,
        options: [...BILLING_STATUS_OPTIONS],
        menus: ["filter", "visible", "groupby", "orderby"] as const,
      },
      {
        name: "location",
        kind: "linked_record",
        menus: ["filter", "visible", "orderby"],
        linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
      },
      { name: "occurred_date", kind: "date", menus: ["filter", "orderby", "groupby", "visible"] },
      { name: "created_at", kind: "date", menus: ["filter", "visible", "orderby", "groupby"] },
    ] as FilterChoice[],
    ...permissions,
    storageKey: "incident_flyout",
  };
};
